'use client'

import type { SearchRequest, SearchResponse, CountRequest, CountResponse, MapPointsRequest, MapPointsResponse, Listing, ListingImage, Location, ComparisonResult, SavedSearch, SavedSearchCreate, SummarizeResponse, ListingAnalytics, NotificationsResponse } from './types'

const API_BASE_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8080'

export interface SharedShortlist {
  owner_name: string
  city?: string
  count: number
  numbers: Record<string, number>
  tags: Record<string, string[]>
  listings: Listing[]
}

const VIDEO_EXTENSIONS = new Set(['mp4', 'webm', 'mov', 'm4v', 'avi', 'mkv', 'wmv', 'flv', 'mpeg', 'mpg'])

function isVideoUrl(url: string): boolean {
  try {
    const path = url.split('?')[0].split('#')[0].toLowerCase()
    const ext = path.includes('.') ? path.slice(path.lastIndexOf('.') + 1) : ''
    return VIDEO_EXTENSIONS.has(ext)
  } catch {
    return false
  }
}

// Sreality serves its photos from Seznam's CDN, which REQUIRES a transform
// param: the bare path 401s, `?fl=...` returns the JPEG. Our scraper stored
// the bare path, so every Sreality listing (the whole sale side of the index)
// rendered as the grey placeholder — the photo silently 401'd and RealPhotos
// fell back to its gradient (user 2026-08-16). This is the exact transform
// sreality.cz itself requests; other presets (res,800,600,1) 400.
const SEZNAM_CDN = /(^|\.)sdn\.cz$/i
const SEZNAM_FL = 'fl=res,1200,1200,1|shr,,20|jpg,80'

function normalizeImageUrl(url: string): string {
  // Protocol-relative `//host/…` — fine on an https page, but on http
  // localhost it resolves to http:// and takes a redirect first.
  let out = url.startsWith('//') ? `https:${url}` : url
  try {
    const u = new URL(out)
    // Only add the param when the URL carries none; a URL that already has
    // its own `fl` (or any query) came through complete.
    if (SEZNAM_CDN.test(u.hostname) && !u.search) out = `${out}?${SEZNAM_FL}`
  } catch {
    /* not absolute — leave as-is */
  }
  return out
}

function sanitizeListingImages(images: ListingImage[] | undefined): ListingImage[] {
  if (!Array.isArray(images)) return []
  return images
    .filter((img) => img?.url && !isVideoUrl(img.url))
    .map((img) => ({ ...img, url: normalizeImageUrl(img.url) }))
}

function sanitizeListing(listing: Listing): Listing {
  return {
    ...listing,
    images: sanitizeListingImages(listing.images),
  }
}

class ApiClient {
  private baseUrl: string
  private token: string | null = null
  private refreshToken: (() => Promise<string | null>) | null = null
  
  constructor(baseUrl: string) {
    this.baseUrl = baseUrl
  }
  
  setToken(token: string | null) {
    this.token = token
  }

  setTokenRefresher(refreshToken: (() => Promise<string | null>) | null) {
    this.refreshToken = refreshToken
  }
  
  private async fetch<T>(
    endpoint: string,
    options: RequestInit = {},
    retryOnUnauthorized = true
  ): Promise<T> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string>),
    }

    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`
    }
    
    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      ...options,
      headers,
    })

    if (response.status === 401 && this.token && this.refreshToken && retryOnUnauthorized) {
      const refreshedToken = await this.refreshToken()
      if (refreshedToken) {
        this.setToken(refreshedToken)
        return this.fetch(endpoint, options, false)
      }
    }
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      
      // Ensure message is always a string
      let message = 'An error occurred'
      if (error.detail) {
        if (typeof error.detail === 'string') {
          message = error.detail
        } else if (typeof error.detail === 'object') {
          message = error.detail.message || error.detail.msg || JSON.stringify(error.detail)
        }
      } else if (error.message) {
        message = typeof error.message === 'string' ? error.message : JSON.stringify(error.message)
      }
      
      throw new ApiError(response.status, message, error)
    }
    
    return response.json()
  }
  
  // Search endpoints
  async search(request: SearchRequest): Promise<SearchResponse> {
    const response = await this.fetch<SearchResponse>('/api/v1/search', {
      method: 'POST',
      body: JSON.stringify(request),
    })
    return {
      ...response,
      results: response.results.map(sanitizeListing),
    }
  }
  
  async countListings(request: CountRequest, signal?: AbortSignal): Promise<CountResponse> {
    return this.fetch<CountResponse>('/api/v1/search/count', {
      method: 'POST',
      body: JSON.stringify(request),
      signal,
    })
  }
  
  /**
   * Every matching listing's coordinates, for the map view. Returns the WHOLE
   * match set (no limit/offset) so the map can cluster client-side instead of
   * paging — zooming then costs no network at all.
   */
  async getMapPoints(request: MapPointsRequest, signal?: AbortSignal): Promise<MapPointsResponse> {
    return this.fetch<MapPointsResponse>('/api/v1/search/map-points', {
      method: 'POST',
      body: JSON.stringify(request),
      signal,
    })
  }

  async getListing(id: string): Promise<Listing> {
    // Sanitized like every other listing path — this one was raw, so the
    // detail page also skipped the video filter, not just the URL fix.
    return sanitizeListing(await this.fetch<Listing>(`/api/v1/listings/${id}`))
  }

  async getListings(ids: string[]): Promise<Listing[]> {
    if (ids.length === 0) return []
    const response = await this.fetch<Listing[]>('/api/v1/listings/batch', {
      method: 'POST',
      body: JSON.stringify({ listing_ids: ids }),
    })
    return response.map(sanitizeListing)
  }
  
  // Cities endpoint
  async getCities(): Promise<string[]> {
    return this.fetch<string[]>('/api/v1/cities')
  }
  
  // Locations endpoint (districts, neighborhoods with autocomplete)
  async getLocations(city: string = '', query: string = '', limit: number = 10): Promise<Location[]> {
    const params = new URLSearchParams()
    if (city) params.set('city', city)
    if (query) params.set('q', query)
    params.set('limit', limit.toString())
    return this.fetch<Location[]>(`/api/v1/locations?${params.toString()}`)
  }
  
  // Geo endpoints
  async geocode(
    query: string,
    country: string = 'CZ',
    provider: 'google' | 'mapbox' = 'google'
  ): Promise<{
    results: Array<{ name: string; lat: number; lon: number }>
  }> {
    return this.fetch('/api/v1/geo/geocode', {
      method: 'POST',
      body: JSON.stringify({ query, country, provider }),
    })
  }

  async getIsochrone(
    address: string,
    minutes: number,
    mode: string = 'driving',
    country: string = 'CZ',
    provider: 'google' | 'mapbox' = 'google'
  ): Promise<{
    center: { lat: number; lon: number }
    polygon: any
    mode: string
    minutes: number
  }> {
    return this.fetch('/api/v1/geo/isochrone', {
      method: 'POST',
      body: JSON.stringify({ address, minutes, mode, country, provider }),
    })
  }
  
  // Comparison endpoint
  async compareListings(
    listingIds: string[],
    userNotes: Record<string, string> = {},
    userRatings: Record<string, number> = {},
    userTags: Record<string, string[]> = {},
    customPrompt?: string,
    userName?: string
  ): Promise<ComparisonResult> {
    return this.fetch<ComparisonResult>('/api/v1/compare', {
      method: 'POST',
      body: JSON.stringify({
        listing_ids: listingIds,
        user_notes: userNotes,
        user_ratings: userRatings,
        user_tags: userTags,
        custom_prompt: customPrompt || null,
        user_name: userName || null,
      }),
    })
  }

  // Summarization endpoint
  async summarizeDescription(text: string, language: string = 'cs'): Promise<SummarizeResponse> {
    return this.fetch<SummarizeResponse>('/api/v1/summarize', {
      method: 'POST',
      body: JSON.stringify({
        text,
        language,
      }),
    })
  }
  
  // Analytics endpoints
  async getListingAnalytics(listingId: string): Promise<ListingAnalytics> {
    return this.fetch<ListingAnalytics>(`/api/v1/analytics/listing/${listingId}`)
  }

  // Board sharing
  async findUserByEmail(email: string): Promise<{
    uid: string
    email: string
    displayName: string
    photoURL: string
  }> {
    return this.fetch(`/api/v1/board-sharing/find-user?email=${encodeURIComponent(email)}`)
  }

  // Public liked-shortlist sharing
  async createSharedShortlist(tags: Record<string, string[]>): Promise<{ token: string; url: string }> {
    return this.fetch('/api/v1/shared', {
      method: 'POST',
      body: JSON.stringify({ tags }),
    })
  }

  async getSharedShortlist(token: string): Promise<SharedShortlist> {
    const response = await this.fetch<SharedShortlist>(`/api/v1/shared/${encodeURIComponent(token)}`)
    return {
      ...response,
      listings: response.listings.map(sanitizeListing),
    }
  }

  async revokeSharedShortlist(token: string): Promise<void> {
    await this.fetch(`/api/v1/shared/${encodeURIComponent(token)}`, { method: 'DELETE' })
  }

  // Liveness endpoints

  /** Build the redirect URL for a listing (opens provider page via our tracking endpoint). */
  getRedirectUrl(listingId: string): string {
    return `${this.baseUrl}/api/v1/listings/${listingId}/redirect`
  }

  async reportUnavailable(listingId: string): Promise<{ status: string; message: string }> {
    return this.fetch(`/api/v1/listings/${listingId}/report-unavailable`, {
      method: 'POST',
    })
  }

  // Health check
  async healthCheck(): Promise<{ status: string; elasticsearch: string }> {
    return this.fetch('/health')
  }
  
  // Saved Searches endpoints
  async createSavedSearch(data: SavedSearchCreate): Promise<SavedSearch> {
    return this.fetch<SavedSearch>('/api/v1/saved-searches', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }
  
  async getSavedSearches(): Promise<SavedSearch[]> {
    return this.fetch<SavedSearch[]>('/api/v1/saved-searches')
  }
  
  async getSavedSearch(id: string): Promise<SavedSearch> {
    return this.fetch<SavedSearch>(`/api/v1/saved-searches/${id}`)
  }
  
  async updateSavedSearch(id: string, updates: { name?: string; is_active?: boolean; roostr_name?: string; cadence?: string }): Promise<SavedSearch> {
    return this.fetch<SavedSearch>(`/api/v1/saved-searches/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    })
  }
  
  async deleteSavedSearch(id: string): Promise<void> {
    await this.fetch<{ message: string }>(`/api/v1/saved-searches/${id}`, {
      method: 'DELETE',
    })
  }

  // Full server-side account deletion (routers/account.py, plan §27.12): ES
  // docs, Firestore, Stripe cancel, then the Auth user — the client cannot do
  // this itself (Admin SDK required). Same endpoint the mobile app uses.
  async deleteAccount(): Promise<void> {
    await this.fetch<{ message: string }>(`/api/v1/account`, {
      method: 'DELETE',
    })
  }

  // (getNewCounts / GET /saved-searches/new-counts was removed as dead code in
  // §26.6 — per-search counts come from getNotificationCountsBySearch, the
  // inbox-backed source both clients share.)

  async markSearchSeen(id: string): Promise<{ last_executed_at: string }> {
    return this.fetch<{ last_executed_at: string }>(`/api/v1/saved-searches/${id}/mark-seen`, {
      method: 'POST',
    })
  }

  // Subscription endpoints

  async getPrices(): Promise<{
    monthly: { id: string; amount: number; currency: string; interval: string; interval_count: number } | null
    quarterly: { id: string; amount: number; currency: string; interval: string; interval_count: number } | null
  }> {
    return this.fetch('/api/v1/subscription/prices')
  }

  async createCheckoutSession(interval: string = 'monthly'): Promise<{ url: string }> {
    return this.fetch<{ url: string }>('/api/v1/subscription/checkout', {
      method: 'POST',
      body: JSON.stringify({ interval }),
    })
  }

  async createPortalSession(): Promise<{ url: string }> {
    return this.fetch<{ url: string }>('/api/v1/subscription/portal', {
      method: 'POST',
    })
  }

  // Notifications endpoints
  async getNotifications(
    limit: number = 20,
    offset: number = 0,
    hydrateListings: boolean = true,
    includePreview: boolean = false
  ): Promise<NotificationsResponse> {
    return this.fetch<NotificationsResponse>(
      `/api/v1/notifications?limit=${limit}&offset=${offset}&hydrate_listings=${hydrateListings ? 'true' : 'false'}&include_preview=${includePreview ? 'true' : 'false'}`
    )
  }

  async getNotificationCountsBySearch(): Promise<Record<string, number>> {
    return this.fetch<Record<string, number>>('/api/v1/notifications/counts-by-search')
  }

  async markNotificationRead(id: string): Promise<void> {
    await this.fetch(`/api/v1/notifications/${id}/read`, { method: 'POST' })
  }

  async markAllNotificationsRead(): Promise<void> {
    await this.fetch('/api/v1/notifications/read-all', { method: 'POST' })
  }
}

export class ApiError extends Error {
  status: number
  details: any
  
  constructor(status: number, message: string, details?: any) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.details = details
  }
  
  get isRateLimited(): boolean {
    return this.status === 429
  }
  
  get isUnauthorized(): boolean {
    return this.status === 401
  }

  get isSubscriptionRequired(): boolean {
    if (this.status !== 403) return false
    const detail = this.details?.detail
    const errorCode = typeof detail === 'object' ? detail?.error : this.details?.error
    return errorCode === 'subscription_required'
  }
  
  /** Seconds until rate limit resets (for 429 errors) */
  get retryAfter(): number | null {
    if (!this.isRateLimited || !this.details?.detail) return null
    return this.details.detail.retry_after || null
  }
  
  /** Rate limit quota information (for 429 errors) */
  get rateLimitInfo(): {
    minuteRemaining: number
    minuteLimit: number
    dayRemaining: number
    dayLimit: number
  } | null {
    if (!this.isRateLimited || !this.details?.detail?.limits) return null
    const limits = this.details.detail.limits
    return {
      minuteRemaining: limits.minute_remaining ?? 0,
      minuteLimit: limits.minute_limit ?? 0,
      dayRemaining: limits.day_remaining ?? 0,
      dayLimit: limits.day_limit ?? 0,
    }
  }
}

// Export singleton instance
export const api = new ApiClient(API_BASE_URL)

// SWR fetcher
export const fetcher = async (url: string) => {
  const response = await fetch(url)
  if (!response.ok) {
    throw new Error('Failed to fetch')
  }
  return response.json()
}

